import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Badge, Alert } from 'react-bootstrap';
import { MapPin, Briefcase, DollarSign, Clock, ArrowLeft, Upload, GraduationCap, Layers, User, Calendar, Factory } from 'lucide-react';
import './JobDetails.css';

const JobDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [cv, setCv] = useState(null);
    const [candidateName, setCandidateName] = useState('');
    const [candidateEmail, setCandidateEmail] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState({ type: '', msg: '' });

    useEffect(() => {
        // fetch(`http://localhost:5000/api/jobs/${id}`)
        fetch(`/api-portal/jobs/${id}`)
            .then(res => {
                if (!res.ok) throw new Error("Job not found");
                return res.json();
            })
            .then(data => setJob(data))
            .catch(err => console.error(err));
    }, [id]);

    const handleApply = async (e) => {
        e.preventDefault();
        setStatus({ type: 'info', msg: 'Processing your application...' });

        // Packaging data in FormData (a must for files)
        const formData = new FormData();
        formData.append('cv', cv);
        formData.append('name', candidateName);
        formData.append('email', candidateEmail);
        formData.append('message', message);
        formData.append('jobId', id);
        formData.append('jobTitle', job.title);

        try {
            // 1. Sending to dashboard (Node.js)
            // const nodeResponse = await fetch("http://localhost:5000/api/apply", {
            const nodeResponse = await fetch("/api-portal/apply", {
                method: "POST",
                body: formData,
            });

            // 2. Sending to PHP (Mail)
            //fetch("http://localhost:5000/api/send-application.php", {
            fetch("/send-application.php", {
                method: "POST",
                body: formData,
            })
                .then(res => res.json())
                .then(res => console.log("PHP Mail Response:", res))
                .catch(e => console.error("PHP Mail Error:", e));

            const data = await nodeResponse.json();
            if (data.success) {
                setStatus({ type: 'success', msg: 'Application submitted successfully' });
                // Reset forme
                setCandidateName('');
                setCandidateEmail('');
                setMessage('');
                setCv(null);
            } else {
                throw new Error("Failed to save application");
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'danger', msg: 'Error submitting application. Please try again.' });
        }
    };

    if (!job) return <div className="text-center py-5">Loading job details...</div>;

    return (
        <div className="job-details-page">
            <Container className="py-5">
                <Button variant="link" onClick={() => navigate(-1)} className="back-btn mb-4">
                    <ArrowLeft size={20} className="me-2" /> Back to Search
                </Button>

                <div className="job-details-wrapper shadow-sm rounded-4 bg-white p-4 p-md-5">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                        <div>
                            <h1 className="display-6 fw-bold mb-1">{job.title}</h1>
                            <span className="text-primary fw-semibold fs-5">{job.category}</span>
                        </div>
                    </div>
                    <Badge bg="info" className="type-badge mt-3 mt-md-0">Vacancy Type: {job.type}</Badge>
                    <hr />

                    <section className="mt-5">
                        <h3 className="section-title mb-3">Job Description</h3>
                        <div className="description-content">
                            <p style={{ whiteSpace: 'pre-wrap' }}>{job.description}</p>
                        </div>
                    </section>

                    <section className="job-summary-grid mt-5">
                        <h3 className="section-title mb-4">Key Details</h3>
                        <Row className="g-3">
                            {[
                                { icon: <MapPin className="text-danger" />, label: "Location", value: job.location },
                                { icon: <DollarSign className="text-success" />, label: "Salary", value: job.salary },
                                { icon: <Clock className="text-primary" />, label: "Job Type", value: job.type },
                                { icon: <Briefcase className="text-secondary" />, label: "Experience", value: job.experience },
                                { icon: <GraduationCap className="text-warning" />, label: "Education", value: job.qualification },
                                { icon: <Layers className="text-info" />, label: "Career Level", value: job.careerLevel },
                                { icon: <User className="text-dark" />, label: "Gender", value: job.gender },
                                { icon: <Factory className="text-dark" color='brown' />, label: "industry", value: job.industry },
                                { icon: <Calendar className="text-muted" />, label: "Posted On", value: new Date(job.createdAt).toLocaleDateString() }
                            ].map((item, idx) => (
                                <Col xs={12} sm={6} lg={3} key={idx}>
                                    <div className="summary-item-card">
                                        <div className="icon-wrapper">{item.icon}</div>
                                        <div className="info-text">
                                            <span className="label-text">{item.label}</span>
                                            <span className="value-text">{item.value}</span>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <hr className="my-5" />

                    <section className="apply-section">
                        <div className="apply-card p-4 p-md-5 rounded-4">
                            <h2 className="text-center fw-bold mb-2">Ready to Apply?</h2>
                            {status.msg && <Alert variant={status.type} className="text-center">{status.msg}</Alert>}

                            <Form onSubmit={handleApply}>
                                <Row className="g-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">Full Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={candidateName}
                                                onChange={(e) => setCandidateName(e.target.value)}
                                                placeholder="First & Last Name" required className="form-control-lg"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">Email Address</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={candidateEmail}
                                                onChange={(e) => setCandidateEmail(e.target.value)}
                                                placeholder="john@example.com" required className="form-control-lg"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">Upload Resume (PDF/DOCX)</Form.Label>
                                            <div className="file-upload-wrapper">
                                                <Form.Control
                                                    type="file"
                                                    required
                                                    onChange={(e) => setCv(e.target.files[0])}
                                                    accept=".pdf,.docx,.doc"
                                                />
                                                <Upload size={20} className="upload-icon" />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">Cover Letter (Optional)</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                placeholder="Tell us about yourself..." className="form-control-lg"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12} className="text-center mt-5">
                                        <Button type="submit" size="lg" className="btn-submit-app px-5">
                                            Submit Application
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    </section>
                </div>
            </Container>
        </div>
    );
};

export default JobDetails;